import { subDays } from "date-fns";
import { formatInTimeZone, fromZonedTime, toZonedTime } from "date-fns-tz";

const format = formatInTimeZone as (date: Date | string | number, tz: string, fmt: string) => string;
const toZoned = toZonedTime as (date: Date | string | number, tz: string) => Date;
const fromZoned = fromZonedTime as (date: Date | string | number, tz: string) => Date;

export function daysAgo(days: number): string {
  return subDays(new Date(), days).toISOString();
}

export function formatTime(isoString: string, timeZone: string): string {
  return format(isoString, timeZone, "HH:mm");
}

export function formatDateHeader(isoString: string, timeZone: string): string {
  const zonedDate = toZoned(isoString, timeZone);
  const nowZoned = toZoned(new Date(), timeZone);

  const entryDay = new Date(zonedDate.getFullYear(), zonedDate.getMonth(), zonedDate.getDate());
  const today = new Date(nowZoned.getFullYear(), nowZoned.getMonth(), nowZoned.getDate());
  const yesterday = new Date(today.getTime() - 86_400_000);

  if (entryDay.getTime() === today.getTime()) return "Today";
  if (entryDay.getTime() === yesterday.getTime()) return "Yesterday";
  return format(isoString, timeZone, "d MMM");
}

export function formatDateTime(isoString: string, timeZone: string): string {
  return format(isoString, timeZone, "d MMM yyyy, HH:mm");
}

/**
 * Returns a date key string suitable for grouping entries by day in the given
 * timezone. Two ISO timestamps sharing the same date key are on the same
 * calendar day in that timezone.
 */
export function getDateKey(isoString: string, timeZone: string): string {
  return format(isoString, timeZone, "yyyy-MM-dd");
}

/**
 * Extracts the time component (HH:mm) from an ISO string interpreted in the
 * given timezone. Used by filter inputs.
 */
export function extractTimeInZone(isoString: string, timeZone: string): string {
  return format(isoString, timeZone, "HH:mm");
}

/**
 * Combines a date ISO string and a time string (HH:mm) in the given timezone,
 * returning a UTC ISO string suitable for API queries.
 *
 * The date string may come from UniversalDatePicker which encodes the user's
 * chosen day as UTC-noon. We extract the calendar date directly from the ISO
 * string (YYYY-MM-DD portion) to avoid timezone-induced day shifts.
 */
export function combineDateAndTimeInZone(isoDate: string, timeStr: string, timeZone: string): string {
  const [hours, minutes] = timeStr.split(":").map(Number);
  const dateOnly = isoDate.slice(0, 10);
  const padH = String(hours ?? 0).padStart(2, "0");
  const padM = String(minutes ?? 0).padStart(2, "0");
  const localDateTimeStr = `${dateOnly}T${padH}:${padM}:00`;
  return fromZoned(localDateTimeStr, timeZone).toISOString();
}

/**
 * Returns the browser's local IANA timezone identifier.
 */
export function getBrowserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Returns the short timezone abbreviation for display (e.g. "AEST", "AWST").
 */
export function getTimezoneAbbreviation(timeZone: string): string {
  return format(new Date(), timeZone, "zzz");
}
